<template>
  <Modal ref="modal" :small="true" :close-button="false" @hidden="reset">
    <div class="template-confirm-modal">
      <div class="template-confirm-modal__header">
        <h2 class="template-confirm-modal__title">
          {{
            installed
              ? $t('templateConfirmModal.installedTitle')
              : $t('templateConfirmModal.title')
          }}
        </h2>
        <p class="template-confirm-modal__description">
          {{
            installed
              ? $t('templateConfirmModal.installedDescription')
              : $t('templateConfirmModal.description')
          }}
        </p>
      </div>

      <div class="template-confirm-modal__summary">
        <div class="template-confirm-modal__summary-row">
          <span class="template-confirm-modal__summary-label">
            {{ $t('templateConfirmModal.templateLabel') }}
          </span>
          <strong>{{ template ? template.name : '' }}</strong>
        </div>
        <div v-if="category" class="template-confirm-modal__summary-row">
          <span class="template-confirm-modal__summary-label">
            {{ $t('templateConfirmModal.categoryLabel') }}
          </span>
          <strong>{{ category.name }}</strong>
        </div>
      </div>

      <div v-if="hasIncludedItems" class="template-confirm-modal__section">
        <h3 class="template-confirm-modal__section-title">
          {{ $t('templateConfirmModal.includedTitle') }}
        </h3>

        <div v-if="tableCount > 0" class="template-confirm-modal__summary-row">
          <span class="template-confirm-modal__summary-label">
            {{ $t('templateConfirmModal.tablesSummaryLabel') }}
          </span>
          <strong>{{ formattedTableCount }}</strong>
        </div>

        <div
          v-if="supportedOperationalViews.length > 0"
          class="template-confirm-modal__views"
        >
          <h4 class="template-confirm-modal__views-title">
            {{ $t('templateConfirmModal.operationalViewsTitle') }}
          </h4>
          <ul class="template-confirm-modal__view-list">
            <li
              v-for="(view, index) in supportedOperationalViews"
              :key="`${view.type}-${view.label}-${index}`"
              class="template-confirm-modal__view-item"
            >
              <span class="template-confirm-modal__view-type">
                {{ operationalViewTypeLabel(view.type) }}
              </span>
              <span class="template-confirm-modal__view-label">
                {{ view.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="confirmingDelete"
        class="template-confirm-modal__delete-confirmation"
      >
        <div class="template-confirm-modal__delete-icon">
          <i class="iconoir-warning-triangle"></i>
        </div>
        <div>
          <h3 class="template-confirm-modal__delete-title">
            {{ $t('templateConfirmModal.deleteInstallation') }}
          </h3>
          <p class="template-confirm-modal__delete-description">
            {{ $t('templateConfirmModal.deleteConfirmation') }}
          </p>
        </div>
      </div>

      <div class="template-confirm-modal__footer">
        <Button type="secondary" :disabled="busy" @click="cancel">
          {{
            confirmingDelete
              ? $t('templateConfirmModal.keepInstallation')
              : $t('action.cancel')
          }}
        </Button>
        <Button
          v-if="installed"
          type="danger"
          :loading="deleting"
          :disabled="busy"
          @click="deleteInstallation"
        >
          {{ $t('templateConfirmModal.deleteInstallation') }}
        </Button>
        <Button
          type="primary"
          :loading="installing"
          :disabled="busy"
          @click="confirm"
        >
          {{
            installed
              ? $t('templateConfirmModal.openInstalled')
              : $t('templateHeader.use')
          }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import Modal from '@baserow/modules/core/components/Modal'
import Button from '@baserow/modules/core/components/Button'

const SUPPORTED_OPERATIONAL_VIEW_TYPES = ['kanban', 'calendar', 'timeline']

export default {
  name: 'TemplateConfirmModal',
  components: { Modal, Button },
  mixins: [modal],
  props: {
    template: {
      required: true,
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    category: {
      required: true,
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    installing: {
      type: Boolean,
      required: true,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    installed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      confirmingDelete: false,
    }
  },
  computed: {
    features() {
      return this.template?.features || {}
    },
    tableCount() {
      const tableNames = Array.isArray(this.features.table_names)
        ? this.features.table_names.filter(Boolean)
        : []

      return Number(this.features.table_count) || tableNames.length
    },
    formattedTableCount() {
      if (this.tableCount === 1) {
        return this.$t('dashboard.templateTableCountOne')
      }

      return this.$t('dashboard.templateTableCountMany', {
        count: this.tableCount,
      })
    },
    supportedOperationalViews() {
      if (!Array.isArray(this.features.operational_views)) {
        return []
      }

      return this.features.operational_views.filter(
        (view) =>
          view?.type &&
          view?.label &&
          SUPPORTED_OPERATIONAL_VIEW_TYPES.includes(view.type)
      )
    },
    hasIncludedItems() {
      return this.tableCount > 0 || this.supportedOperationalViews.length > 0
    },
    busy() {
      return this.installing || this.deleting
    },
  },
  methods: {
    reset() {
      this.confirmingDelete = false
    },
    cancel() {
      if (this.confirmingDelete) {
        this.confirmingDelete = false
        return
      }

      this.hide()
    },
    confirm() {
      if (this.busy) {
        return
      }

      this.$emit(this.installed ? 'open' : 'confirm')
    },
    deleteInstallation() {
      if (this.busy) {
        return
      }

      if (!this.confirmingDelete) {
        this.confirmingDelete = true
        return
      }

      this.$emit('delete')
    },
    operationalViewTypeLabel(type) {
      const key = `templateHeader.operationalViewTypes.${type}`
      return this.$te(key) ? this.$t(key) : type
    },
  },
}
</script>
